// **The production `TokenAcquirer`: `@azure/msal-browser`** (`client-app`, design **D16**/**D20**).
//
// The client's first authentication dependency, and the counterpart to the server's first JWT one.
// It is reached **only** through this module, and this module is imported dynamically, so no test
// imports MSAL: every bootstrap and HTTP-layer scenario is asserted against the injected
// `TokenAcquirer` seam instead.
//
// # Where the tenant and application ids come from
//
// Build-time (`import.meta.env`), not the server. `server-entra-auth` forbids `GET /auth/me` from
// disclosing the tenant id or the pinned audience, so the identity surface cannot carry them, and
// every other path under `/auth` is gated — a configuration route would have to be a third
// exemption for the sake of two public identifiers that are already visible in every token this
// client sends. They are baked into the bundle instead, which is what every other MSAL SPA does.
//
// The cost is honest and worth stating: **changing tenant needs an image rebuild**, not a
// `terraform apply`. The server's own posture (design **D12**) is deliberately on the faster path;
// the client's copy of the public ids is not, so a tenant migration is a deploy, not a variable.

import { INERT_ACQUIRER, type TokenAcquirer } from './auth-token';

/** The scope the registration exposes (design **D16**): `api://<client-id>/access_as_user`,
 * pre-authorised to the same client. Requesting the *exposed scope* rather than the ID token is
 * what makes the server's `aud` pin mean what a reader takes it to mean. */
const SCOPE_SUFFIX = 'access_as_user';

export interface MsalConfig {
  tenantId: string;
  clientId: string;
}

/** The build-time configuration, or `null` when this bundle was not built for a tenant — which is
 * every self-hosted and local build, and is not an error. */
export function msalConfigFromEnv(
  env: { VITE_ENTRA_TENANT_ID?: string; VITE_ENTRA_CLIENT_ID?: string } = import.meta.env,
): MsalConfig | null {
  const tenantId = (env.VITE_ENTRA_TENANT_ID ?? '').trim();
  const clientId = (env.VITE_ENTRA_CLIENT_ID ?? '').trim();
  return tenantId !== '' && clientId !== '' ? { tenantId, clientId } : null;
}

/**
 * The real acquirer, or the inert one when this bundle carries no tenant.
 *
 * `@azure/msal-browser` is loaded with a dynamic `import()` so it stays out of the initial chunk
 * and out of every test: a build with no tenant never evaluates it at all.
 */
export async function createMsalAcquirer(
  config: MsalConfig | null = msalConfigFromEnv(),
): Promise<TokenAcquirer> {
  if (config === null) {
    return INERT_ACQUIRER;
  }
  const { PublicClientApplication } = await import('@azure/msal-browser');
  const scopes = [`api://${config.clientId}/${SCOPE_SUFFIX}`];
  const app = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
    },
    // `sessionStorage`, not `localStorage`: the residual-access window is already the tenant's
    // (design **D20**) and is documented as such, so there is no reason to widen it to every tab
    // this browser will ever open.
    cache: { cacheLocation: 'sessionStorage' },
  });
  await app.initialize();
  // A redirect that has just landed carries its result on this load; consuming it here is what
  // turns "the player came back from Entra" into an account MSAL can acquire silently against.
  await app.handleRedirectPromise();

  const account = () => app.getAllAccounts()[0] ?? null;

  return {
    acquireSilent: async () => {
      const held = account();
      if (held === null) {
        return null;
      }
      try {
        const result = await app.acquireTokenSilent({ scopes, account: held });
        return result.accessToken;
      } catch {
        // Every silent failure means the same thing to the caller: ask the player.
        return null;
      }
    },
    signIn: async () => {
      await app.loginRedirect({ scopes });
    },
    signOut: async () => {
      const held = account();
      await app.logoutRedirect(held === null ? {} : { account: held });
    },
  };
}
